import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../firebase";
import { onSnapshot, doc as docRef } from "firebase/firestore";
import { CircularProgress, Box, Typography } from "@mui/material";

const LOGGING_OUT_FLAG = "loggingOutNow";

const normalizeRoleList = (input) => {
  if (Array.isArray(input)) {
    return input
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
  }

  if (typeof input === "string") {
    const trimmed = input.trim().toLowerCase();
    return trimmed ? [trimmed] : [];
  }

  if (input && typeof input === "object") {
    return Object.entries(input)
      .filter(([, enabled]) => enabled === true)
      .map(([role]) => String(role || "").trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
};

const ProtectedRoute = ({ children, requiredRoles }) => {
  const [user, loadingAuth] = useAuthState(auth);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingTokenClaims, setLoadingTokenClaims] = useState(true);
  const [profileRoles, setProfileRoles] = useState([]);
  const [tokenRoles, setTokenRoles] = useState([]);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setProfileRoles([]);
      setTokenRoles([]);
      setLoadingProfile(false);
      setLoadingTokenClaims(false);
      return;
    }

    setLoadingProfile(true);
    setLoadingTokenClaims(true);

    user
      .getIdTokenResult(true)
      .then((result) => {
        if (cancelled) return;
        const claims = result?.claims || {};
        const rolesFromToken = normalizeRoleList(claims?.roles);
        const rolesFromNested = normalizeRoleList(claims?.customClaims?.roles);
        const rolesFromBooleanAdmin = claims?.admin === true ? ["admin"] : [];
        setTokenRoles(
          Array.from(new Set([...rolesFromToken, ...rolesFromNested, ...rolesFromBooleanAdmin]))
        );
      })
      .catch((err) => {
        console.warn("Failed to read token roles:", err);
        if (!cancelled) setTokenRoles([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTokenClaims(false);
      });

    const ref = docRef(db, "users", user.uid);
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const data = snapshot.data() || {};
        const rolesFromProfile = normalizeRoleList(data?.customClaims?.roles);
        const rolesFromLegacyRoot = normalizeRoleList(data?.roles);
        const rolesFromLegacyAdminFlag = data?.isAdmin === true ? ["admin"] : [];
        setProfileRoles(
          Array.from(
            new Set([...rolesFromProfile, ...rolesFromLegacyRoot, ...rolesFromLegacyAdminFlag])
          )
        );
        setLoadingProfile(false);
      },
      (err) => {
        console.error("Failed to fetch user roles:", err);
        setProfileRoles([]);
        setLoadingProfile(false);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user]);

  if (loadingAuth || loadingProfile || loadingTokenClaims) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    const loggingOut = sessionStorage.getItem(LOGGING_OUT_FLAG) === "1";
    if (loggingOut) {
      return <Navigate to="/" replace />;
    }
    // Thread the intended destination (path + query) through ?next so it
    // survives both sign-in AND sign-up. location.state alone is dropped when
    // the sign-in page hands off to sign-up, which loses deep-link context
    // like /membership?returnTo=/capsule/:id.
    const nextTarget = `${location.pathname}${location.search || ""}`;
    return (
      <Navigate
        to={`/signin?next=${encodeURIComponent(nextTarget)}`}
        state={{ from: location }}
        replace
      />
    );
  }

  if (Array.isArray(requiredRoles)) {
    const effectiveRoles = Array.from(new Set([...(profileRoles || []), ...(tokenRoles || [])]));
    const hasRequiredRole = requiredRoles.some((role) =>
      effectiveRoles.includes(String(role || "").trim().toLowerCase())
    );
    if (!hasRequiredRole) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" color="error" gutterBottom>
            Unauthorized
          </Typography>
          <Typography>You do not have permission to view this page.</Typography>
        </Box>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
